import { useEffect, useMemo } from 'react';

import { Link, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { DeletionDetail } from './deletions/:deletionId';
import { FileLog } from './uploads/file-log';
import { SettingsHeader } from '../components/settings-header';
import { UploadExplainerModal } from './components/modals/upload-explainer-modal';
import { Overview } from './overview';
import { FailedDocuments } from './runs/:runId';
import { RunLog } from './runs/run-log';
import { UploadFailures } from './uploads/:uploadId';
import { TrackingEventsV2, PageName } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';

enum View {
  Overview = 'overview',
  FileLog = 'file-log',
  RunLog = 'run-log',
}

export function Uploads() {
  const { matterId } = useParams();
  const location = useLocation();
  if (!matterId) return null;

  // Determine active tab from URL
  const activeTab = useMemo(() => {
    if (location.pathname.includes('file-log')) {
      return View.FileLog;
    } else if (location.pathname.includes('run-log')) {
      return View.RunLog;
    }
    return View.Overview;
  }, [location.pathname]);

  useEffect(() => {
    trackPageView(PageName.FileHistory);
  }, []);

  return (
    <>
      <SettingsHeader
        title="File History"
        subheading={
          <div className="flex flex-col gap-1">
            <p className="text-gray-600">View a history of all of the files uploaded and processed in this Matter.</p>
            <p className="text-xs text-gray-500">
              Statistics before 9th February 2026 may be inaccurate as they predate more accurate tracking.
            </p>
          </div>
        }
        action={<UploadExplainerModal />}
      />

      <div className="mb-4 flex items-center justify-between">
        <ButtonGroup>
          <Button
            className={`py-1.5 ${activeTab === View.Overview ? 'bg-gray-100 text-gray-900' : 'text-gray-600'}`}
            asChild
          >
            <Link
              to=""
              className="flex items-center gap-2"
              onClick={() => trackCustomEvent(TrackingEventsV2.FileHistoryTabChanged, { matterId, tab: 'overview' })}
            >
              <Icon
                className={`lucide--git-branch h-4 w-4 ${activeTab === View.Overview ? 'text-gray-700' : 'text-gray-500'}`}
              />
              Overview
            </Link>
          </Button>
          <Button
            className={`py-1.5 ${activeTab === View.FileLog ? 'bg-gray-100 text-gray-900' : 'text-gray-600'}`}
            asChild
          >
            <Link
              to="file-log"
              className="flex items-center gap-2"
              onClick={() => trackCustomEvent(TrackingEventsV2.FileHistoryTabChanged, { matterId, tab: 'file-log' })}
            >
              <Icon
                className={`lucide--files h-4 w-4 ${activeTab === View.FileLog ? 'text-gray-700' : 'text-gray-500'}`}
              />
              File History Log
            </Link>
          </Button>
          <Button
            className={`py-1.5 ${activeTab === View.RunLog ? 'bg-gray-100 text-gray-900' : 'text-gray-600'}`}
            asChild
          >
            <Link
              to="run-log"
              className="flex items-center gap-2"
              onClick={() => trackCustomEvent(TrackingEventsV2.FileHistoryTabChanged, { matterId, tab: 'run-log' })}
            >
              <Icon
                className={`lucide--activity h-4 w-4 ${activeTab === View.RunLog ? 'text-gray-700' : 'text-gray-500'}`}
              />
              Run History Log
            </Link>
          </Button>
        </ButtonGroup>
      </div>

      <div className="pb-2">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="file-log" element={<FileLog />} />
          <Route path="file-log/uploads/:uploadId" element={<UploadFailures />} />
          <Route path="run-log" element={<RunLog />} />
          <Route path="runs/:runId" element={<FailedDocuments />} />
          <Route path="file-log/deletions/:deletionId" element={<DeletionDetail />} />
        </Routes>
      </div>
    </>
  );
}
